import { ChangeEvent, useEffect, useState } from "react";
import { Button, Input } from "reactstrap";
import { SelectPicker } from "rsuite";

import useGenerateEmail, {
  GenerateEmailPayload,
} from "../../../../api/api-hooks/brie/useGenerateEmail";
import AppSpinner from "../../../../elements/AppSpinner";
import AppCopy from "../../../../components/AppComponents/AppCopy";
import EmailDrawer from "../../../../components/Drawers/EmailDrawer";

const toneOptions = [
  "Assertive",
  "Conversational",
  "Formal",
  "Friendly",
  "Funny",
  "Humorous",
  "Informal",
  "Motivating",
  "Optimistic",
  "Professional",
  "Respectful",
  "Serious",
  "Urgent",
];
const companyTypeOptions = [
  "Technology",
  "Bank",
  "Insurance",
  "Retail",
  "Transportation",
];

interface Props {
  providedStateValue?: string;
  providedStateSetter?: React.Dispatch<React.SetStateAction<string>>;
  createEmailButton?: boolean;
  simplified?: boolean;
}

/**
 * Email Generator AI Model
 */
const EmailGenerator = ({
  providedStateValue,
  providedStateSetter,
  createEmailButton,
  simplified,
}: Props) => {
  const [generatedEmailState, setGeneratedEmailState] = useState("");
  const [tone, setTone] = useState("");
  const [companyType, setCompanyType] = useState("");
  const [description, setDescription] = useState("");
  const [wordCount, setWordCount] = useState<number | "">("");
  const [includeEmojis, setIncludeEmojis] = useState(false);
  const [emailDrawer, setEmailDrawer] = useState(false);

  const generatedEmail = providedStateValue || generatedEmailState;
  const setGeneratedEmail = providedStateSetter || setGeneratedEmailState;

  const { generateEmail, generating } = useGenerateEmail();

  // Scroll to bottom when email finishes generating.
  useEffect(() => {
    if (!generatedEmail) return;

    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });
  }, [generatedEmail]);

  const EmailGeneratorBody = (
    <>
      <p className="text-sm text-dark mb-1 font-weight-bold">
        Email Voice Tone
      </p>
      <SelectPicker
        data={toneOptions.map((o) => ({ label: o, value: o }))}
        value={tone}
        onChange={(newTone) => setTone(String(newTone))}
        placeholder="Select Tone"
        searchable={false}
        disabled={generating}
      />

      <p className="text-sm text-dark mt-4 mb-1 font-weight-bold">
        Company Type
      </p>
      <SelectPicker
        data={companyTypeOptions.map((o) => ({ label: o, value: o }))}
        value={companyType}
        onChange={(newTone) => setCompanyType(String(newTone))}
        placeholder="Select Company Type"
        searchable={false}
        disabled={generating}
      />
      <p className="text-sm text-dark mt-4 mb-1 font-weight-bold">
        Description
      </p>
      <Input
        type="textarea"
        rows={5}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Example: Savings Accounts, where customers can deposit money and earn interest on their savings"
        disabled={generating}
      />
      <p className="text-sm text-dark mt-4 mb-1 font-weight-bold">
        Setup Word Count
      </p>
      <Input
        type="number"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const newCount = e.target.value;

          if (Number(newCount) > 400) return;

          setWordCount(parseInt(newCount));
        }}
        className="form-control"
        value={wordCount}
        placeholder="Number of words in the generated email. Limit 400"
        bsSize="sm"
        disabled={generating}
      />
      <div
        className="d-flex align-items-center text-dark text-sm my-4 cursor-pointer no-select"
        onClick={() => setIncludeEmojis((prev) => !prev)}
      >
        <Input
          type="checkbox"
          style={{ position: "unset" }}
          className="m-0 mr-2"
          checked={includeEmojis}
          disabled={generating}
        />
        Include emojis{" "}
        <span className="ml-2" style={{ fontSize: 18 }}>
          &#129321;
        </span>
      </div>
      <div className="d-flex justify-content-end">
        {generating ? (
          <AppSpinner size="xl" />
        ) : (
          <>
            <Button size="sm" outline onClick={restart}>
              Restart
            </Button>
            <Button
              size="sm"
              color="primary"
              onClick={onGenerateEmail}
              disabled={
                generating ||
                !tone ||
                !companyType ||
                !description ||
                !wordCount
              }
            >
              Generate Email
            </Button>
          </>
        )}
      </div>
      {generatedEmail && (
        <>
          <GeneratedText title="Generated Email:" text={generatedEmail} />
          {createEmailButton && (
            <div className="d-flex justify-content-end mt-4 mb-4">
              <Button
                size="sm"
                className="d-flex align-items-center"
                onClick={() => setEmailDrawer(true)}
              >
                <i
                  className="mdi mdi-email-fast-outline mr-2"
                  style={{ fontSize: 16 }}
                />
                Create Email
              </Button>
              <EmailDrawer
                open={emailDrawer}
                onClose={() => setEmailDrawer(false)}
                email={generatedEmail}
              />
            </div>
          )}
        </>
      )}
    </>
  );

  return simplified ? (
    EmailGeneratorBody
  ) : (
    <ToolWrapper
      title="Email Generator"
      description="This AI model analyzes your input and generates draft emails within
        seconds."
      icon="email-fast-outline"
    >
      {EmailGeneratorBody}
    </ToolWrapper>
  );

  // Reset all state
  function restart() {
    setTone("");
    setCompanyType("");
    setWordCount("");
    setDescription("");
    setIncludeEmojis(false);
    setGeneratedEmail("");
  }

  function onSuccess(email: string) {
    setGeneratedEmail(email);
  }

  function onGenerateEmail() {
    if (typeof wordCount !== "number") return;

    const payload: GenerateEmailPayload = {
      tone,
      companyType,
      description,
      wordCount,
      includeEmojis,
    };

    generateEmail({ payload, onSuccess });
  }
};

export function ToolWrapper({
  icon,
  title,
  description,
  children,
}: {
  icon: string;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="d-flex flex-column"
      style={{
        boxShadow: "0px 1px 8px 2px rgba(0, 0, 0, 0.15)",
        padding: "24px",
        borderRadius: "8px",
        width: "100%",
      }}
    >
      <div className="d-flex w-100 align-items-center mb-0">
        <i className={`mdi mdi-${icon} mr-3`} style={{ fontSize: "32px" }}></i>
        <h2 className="m-0">{title}</h2>
      </div>
      <p className="small text-dark mb-1">{description}</p>
      {children}
    </div>
  );
}

export function GeneratedText({
  title,
  text,
  isRichText,
  copyable = true,
}: {
  title: string;
  text: string;
  isRichText?: boolean;
  copyable?: boolean;
}) {
  return (
    <>
      <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
        <h4 className="text-primary mb-0">{title}</h4>
        {copyable && <AppCopy text={text} />}
      </div>
      <div
        style={{ whiteSpace: "pre-wrap" }}
        className={`border p-2 border-primary text-sm rounded ${
          isRichText ? "ql-editor document-inner rich-text-preview" : ""
        }`}
        dangerouslySetInnerHTML={isRichText ? { __html: text } : undefined}
      >
        {isRichText ? undefined : text}
      </div>
    </>
  );
}

export default EmailGenerator;
